import Check from "@mui/icons-material/Check";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  ExtractionStatus,
  ProcessedManifestsQuery,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import { DateTime } from "luxon";
import { OmnisearchProvider } from "providers/OmnisearchProvider";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { YearAutocompletePicker } from "../ManifestLineItems/YearAutocompletePIcker";
import { AddManifestButton } from "./AddManifestButton";
import { PROCESSED_MANIFESTS_QUERY } from "./api";
import { DiscrepanciesCell } from "./DiscrepanciesCell";
import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { EManifestCell } from "./EManifestCell";
import { ManifestDetails } from "./ManifestDetails";
import { ManifestsExportMenu } from "./ManifestsExportMenu";
import { ManifestShowFilterPicker } from "./ManifestShowFilterPicker";
import { ManifestStatsCardsConnected } from "./ManifestStatsCards";
import { SourcesCell } from "./SourcesCell";
import { TransitCell } from "./TransitCell";
import { VendorDashboard } from "./VendorDashboard/VendorDashboard";
import { VendorDashboardToggle } from "./VendorDashboard/VendorDashboardToggle";

export type Row = ProcessedManifestsQuery["processedManifests"]["items"][number];

const DOCUMENT_STATES = ["needsReview", "failed", "attached", "approved", "none"] as const;
type DocumentState = (typeof DOCUMENT_STATES)[number];

const DOCUMENT_STATE_LABELS: Record<DocumentState, string> = {
  needsReview: "Needs review",
  failed: "Failed",
  attached: "Attached",
  approved: "Approved",
  none: "None",
};

/**
 * Descriptive document status shown inline in the Sources tooltip's Document
 * line (e.g. "Document: reviewed"). `null` means there's no extra detail to
 * show, so the tooltip falls back to plain present/missing.
 */
const DOCUMENT_STATE_SOURCE_HOVER: Record<DocumentState, string | null> = {
  needsReview: "needs review",
  failed: "import failed",
  attached: "attached",
  approved: "reviewed",
  none: null,
};

/**
 * MUI color token per document state — keeps the at-a-glance severity the icons
 * used to convey, shared by the Document column text and the Sources tooltip.
 */
const DOCUMENT_STATE_COLOR: Record<DocumentState, string> = {
  needsReview: "warning.main",
  failed: "error.main",
  attached: "text.secondary",
  approved: "success.main",
  none: "text.secondary",
};

function getDocumentState(row: Row): DocumentState {
  const sourceDocument = row.sourceDocument;
  if (!sourceDocument) return "none";
  if (sourceDocument.extractionStatus === ExtractionStatus.Failed) return "failed";
  if (sourceDocument.documentMetadata?.needsReview) return "needsReview";
  if (sourceDocument.extractionStatus === ExtractionStatus.Succeeded) return "approved";
  return "attached";
}

/**
 * Manifests list page.
 *
 * Top-level wrapper handles the page chrome (year + Show picker, breadcrumbs) and
 * hoists state into `OmnisearchProvider` so the stats cards and grid share
 * omnisearch state.
 *
 * The actual layout (chips, cards, grid) lives in `ManifestsContent` so the
 * provider can supply omnisearch state to all of its children via context.
 */
export function Manifests() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const persistenceKey = "waste-manifests";
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLeftNavV2 = featureFlags[FeatureFlagKey.LeftNavV2];
  const showDiscrepancyCounts = isStaff && featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];
  const showSourceDocument = isStaff && !!featureFlags[FeatureFlagKey.ManifestDocumentIngest];

  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear]
  );

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifests",
    },
  ]);

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("processedManifestId");

  const columns = useManifestColumns({
    showDiscrepancyCounts,
    showSourceDocument,
  });

  return (
    <Box>
      <OmnisearchProvider
        persistenceKey={persistenceKey}
        columns={columns}
        isURLDriven
        initialSortModel={[
          ...(showSourceDocument ? [{ field: "sourceDocumentState", sort: "asc" as const }] : []),
          { field: "shippedDate", sort: "desc" as const },
        ]}
        initialColumnVisibilityModel={{
          isCertified: false,
          hasSource: false,
          // Document status now lives in the Sources tooltip; keep the column
          // available in column management but hidden by default.
          sourceDocumentState: false,
          // The Transit column rolls these three up; keep them available in
          // column management / FILTERS but hide them from the default view.
          shippedDate: false,
          receivedDate: false,
          eManifestDaysInTransit: false,
          ...(isMobile && {
            epaId: false,
            wasteLineCount: false,
            vendor: false,
          }),
        }}
      >
        <SubNavAppBar
          sx={{
            py: 2,
            px: 3,
            borderBottom: "1px solid",
            borderColor: theme.palette.divider,
            ...(!isLeftNavV2 && {
              top: {
                xs: theme.spacing(xsHeight + tabsOffset),
                md: theme.spacing(mdHeight + tabsOffset),
              },
            }),
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 80 }}>
                <YearAutocompletePicker
                  label="Year"
                  minYear={2018}
                  value={shippedDateYear}
                  onChange={setShippedDateYear}
                />
              </Box>
              <ManifestShowFilterPicker defaultSearch={defaultSearch} />
            </Stack>
            <VendorDashboardToggle />
          </Stack>
        </SubNavAppBar>
        <VendorDashboard shippedDateYear={shippedDateYear} />
        <ManifestsContent
          tenantId={tenantId ?? ""}
          defaultSearch={defaultSearch}
          persistenceKey={persistenceKey}
          shippedDateYear={shippedDateYear}
          columns={columns}
          showDiscrepancyCounts={showDiscrepancyCounts}
          pushIdToUrl={pushIdToUrl}
        />
      </OmnisearchProvider>
      {/*
        The detail drawer (and its line-items grid) renders OUTSIDE the
        OmnisearchProvider on purpose. OmnisearchDataGrid reuses any ancestor
        OmnisearchProvider's grid state, so nesting the drawer inside would make
        the line-items grid inherit the manifests grid's sort/columns (e.g.
        `sourceDocumentState`) and crash the `wasteLinesUnified` query. Keeping
        it a sibling gives the line-items grid its own independent state.
      */}
      <ManifestDetails
        processedManifestId={selectedId}
        open={!!selectedId}
        onClose={popIdFromUrl}
      />
    </Box>
  );
}

interface ManifestsContentProps {
  tenantId: string;
  defaultSearch: string;
  persistenceKey: string;
  shippedDateYear: number;
  columns: OmnisearchGridColDef<Row>[];
  showDiscrepancyCounts: boolean;
  pushIdToUrl: (id: string) => void;
}

function ManifestsContent({
  tenantId,
  defaultSearch,
  persistenceKey,
  shippedDateYear,
  columns,
  showDiscrepancyCounts,
  pushIdToUrl,
}: ManifestsContentProps) {
  const theme = useTheme();
  const greyBackground = useThemeGreyBackgroundColor();
  const apiRef = useGridApiRef();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkReviewDialog, setShowBulkReviewDialog] = useState(false);

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  return (
    <Box
      sx={{
        background: greyBackground,
        px: { xs: 2, md: 3 },
        py: 3,
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
        },
      }}
    >
      <Stack spacing={2}>
        {/* Thin stats section — pale-gray stat boxes on their own white card. */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            p: 2,
          }}
        >
          <ManifestStatsCardsConnected defaultSearch={defaultSearch} />
        </Paper>
        {/* Grid section — its own white card holding the omnisearch + grid. */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            // Breathing room above the omnisearch bar and below the grid footer.
            py: 2,
          }}
        >
          <OmnisearchDataGrid
            apiRef={apiRef}
            persistenceKey={persistenceKey}
            isURLDriven
            columns={columns}
            dataQuery={PROCESSED_MANIFESTS_QUERY}
            getItems={(data) => data.processedManifests.items}
            getCount={(data) => data.processedManifests.count}
            noDataMessage={`No manifests have been added to this organization.`}
            checkboxSelection={showDiscrepancyCounts}
            onSelectedIdsChanged={
              showDiscrepancyCounts ? (ids) => setSelectedIds(ids.map(String)) : undefined
            }
            disableRowSelectionOnClick
            onRowClick={({ row: { id } }) => pushIdToUrl(id)}
            defaultSearch={defaultSearch}
            // Compact rows match the rest of Oak; matches the Figma "compact and
            // terse" direction. Auto-height was forgiving but ate vertical space.
            rowHeight={38}
            commandButtons={({ search }) => [
              ...(showDiscrepancyCounts && selectedIds.length > 0
                ? [
                    <Button
                      key="review-discrepancies"
                      variant="outlined"
                      color="warning"
                      startIcon={<CompareArrowsIcon />}
                      onClick={() => setShowBulkReviewDialog(true)}
                    >
                      Review Discrepancies ({selectedIds.length})
                    </Button>,
                  ]
                : []),
              <AddManifestButton key="add-manifest" tenantId={tenantId} />,
              <ManifestsExportMenu
                key="export"
                tenantId={tenantId}
                fileName={filename}
                search={search}
                selectedIds={selectedIds}
                showDiscrepancyCounts={showDiscrepancyCounts}
              />,
            ]}
            showFilters
            additionalFilterColumns={[
              {
                header: "State/Territory",
                key: "states",
                filterKeyType: "enumAutocomplete",
                enumValues: Object.keys(STATE_NAMES),
                enumPresentationFunction: (value: string) => {
                  const name = STATE_NAMES[value as keyof typeof STATE_NAMES];
                  return name ? `${name} (${value})` : value;
                },
              },
            ]}
            excludeFilterColumns={["wasteLineCount"]}
          />
        </Paper>
        {showBulkReviewDialog && selectedIds.length > 0 && (
          <DiscrepancyReviewDialog
            processedManifestIds={selectedIds}
            onClose={() => setShowBulkReviewDialog(false)}
          />
        )}
      </Stack>
    </Box>
  );
}

interface UseManifestColumnsArgs {
  showDiscrepancyCounts: boolean;
  showSourceDocument: boolean;
}

/**
 * Column definitions for the manifests grid.
 *
 * Splits the table into three readable groups:
 *
 * 1. **Identity**: tracking #, EPA ID, facility.
 * 2. **Provenance + lifecycle** — two cleanly separated deltas plus source
 *    availability: Sources (3-pill availability indicator; tooltip lists each
 *    source and the days-awaiting count on a missing e-Manifest), Transit
 *    (two-line Shipped / Received + transit days, rolling up the hidden ship
 *    date / received date / days-in-transit columns), and e-Manifest (the
 *    awaiting-days countdown with 45/60-day color + compliance action state;
 *    sorts/filters on the `daysSinceShipment` ordinal).
 * 3. **Filtering-only**: vendor, isCertified, hasSource, document state, plus
 *    the three Transit source columns — these stay around so the FILTERS
 *    popover and column management keep existing audit muscle memory; most are
 *    hidden by default. (e-Manifest presence is shown by Sources and filtered
 *    via `hasSource`; the Show picker's overdue/awaiting presets apply
 *    `isEManifest` as raw omnisearch terms, so no grid column is needed.)
 *
 * Compare Sources is reached from the manifest drawer, not a grid column.
 */
function useManifestColumns({
  showDiscrepancyCounts,
  showSourceDocument,
}: UseManifestColumnsArgs): OmnisearchGridColDef<Row>[] {
  const minWidth = 120;

  return useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        minWidth,
        flex: 1,
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        minWidth,
        flex: 1,
      },
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        minWidth,
        flex: 1,
        valueGetter: (_, row) => row.facility?.name ?? "",
      },
      {
        field: "sources",
        headerName: "Sources",
        minWidth: 96,
        width: 96,
        flex: 0,
        sortable: false,
        filterable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <SourcesCell
            availableSources={row.availableSources ?? []}
            // Only uniform-hazardous manifests are actually awaiting a required
            // e-Manifest, so only they get the days count (matches EManifestCell).
            daysAwaitingEManifest={row.isUniformHazardousManifest ? row.daysSinceShipment : null}
            documentStatusLabel={DOCUMENT_STATE_SOURCE_HOVER[getDocumentState(row)]}
          />
        ),
      },
      {
        // Combines Ship Date / Received Date / Days in transit into one
        // two-line cell. The three underlying columns still exist (below) for
        // the FILTERS popover and column management, but are hidden by default.
        field: "transit",
        headerName: "Transit",
        minWidth: 160,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => <TransitCell input={row} />,
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => {
          return value ? getUtcDisplayDate(value, "med") : null;
        },
      },
      {
        field: "receivedDate",
        headerName: "Received Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => (value ? getUtcDisplayDate(value, "med") : null),
      },
      {
        field: "eManifestDaysInTransit",
        headerName: "Days in transit",
        minWidth,
        flex: 1,
        filterKeyType: "number",
      },
      {
        // Single e-Manifest compliance column. Sorts/filters on the raw
        // days-since-shipment ordinal; the cell renders "X days" with the
        // 45/60-day color scale plus the current compliance action state
        // (folding in the retired Status column's transporter-contacted /
        // exception-report signals). Only uniform-hazardous manifests still
        // awaiting their e-Manifest render a value.
        field: "daysSinceShipment",
        headerName: "e-Manifest",
        minWidth: 132,
        flex: 1,
        filterKeyType: "number",
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<Row>) => <EManifestCell input={row} />,
      },
      {
        field: "wasteLineCount",
        headerName: "Line Items",
        flex: 0.6,
        minWidth: 90,
      },
      ...(showDiscrepancyCounts
        ? [
            {
              field: "discrepancies" as const,
              headerName: "Discrepancies",
              flex: 0.8,
              minWidth: 112,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: GridRenderCellParams<Row>) => (
                <DiscrepanciesCell
                  unresolvedCount={row.unresolvedDiscrepancyCount ?? 0}
                  pendingCount={row.pendingDiscrepancyCount ?? 0}
                />
              ),
            },
          ]
        : []),

      // -------- Filtering-only / legacy columns (hidden by default) --------
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
      },
      {
        field: "isCertified",
        headerName: "Certified",
        flex: 1,
        sortable: true,
        filterKeyType: "boolean",
        renderCell: ({ row }) => (row.isCertified ? <Check fontSize="small" /> : null),
      },
      {
        field: "hasSource",
        headerName: "Has source",
        flex: 1,
        sortable: false,
        filterKeyType: "enum",
        enumValues: [
          WasteManifestSource.EManifest,
          WasteManifestSource.Vendor,
          WasteManifestSource.ManifestDocument,
        ],
        enumPresentationFunction: (value: string) => {
          if (value === WasteManifestSource.EManifest) return "e-Manifest";
          if (value === WasteManifestSource.Vendor) return "Vendor";
          if (value === WasteManifestSource.ManifestDocument) return "Document";
          return value;
        },
        // No cell — this column exists strictly for the filter UI; its
        // visibility is forced off in `initialColumnVisibilityModel` above.
        renderCell: () => null,
      },
      ...(showSourceDocument
        ? [
            {
              field: "sourceDocumentState" as const,
              headerName: "Document",
              minWidth: 120,
              flex: 0.5,
              align: "left" as const,
              headerAlign: "left" as const,
              sortable: true,
              filterable: true,
              filterKeyType: "enum" as const,
              enumValues: [...DOCUMENT_STATES],
              enumPresentationFunction: (value: string) => {
                const labels: Record<string, string> = DOCUMENT_STATE_LABELS;
                return labels[value] ?? value;
              },
              valueGetter: (_: unknown, row: Row) => getDocumentState(row),
              renderCell: ({ row }: GridRenderCellParams<Row>) => {
                const state = getDocumentState(row);
                if (state === "none") return null;
                // Text instead of icons, matching the filter labels; color keeps
                // the at-a-glance severity the icons used to convey.
                return (
                  <Typography variant="body2" noWrap sx={{ color: DOCUMENT_STATE_COLOR[state] }}>
                    {DOCUMENT_STATE_LABELS[state]}
                  </Typography>
                );
              },
            },
          ]
        : []),
    ],
    [showDiscrepancyCounts, showSourceDocument]
  );
}
